import { Injectable } from '@angular/core';
import { TypOdbiorcy } from '@interfaces/komunikacja/enums/typy-odbiorcy.enum';
import { GrupaOdbiorcowDto } from '@interfaces/komunikacja/grupa-odbiorcow.interface';
import { OdbiorcaDto } from '@interfaces/komunikacja/komunikacja.interface';
import { UzytkownikIGrupaOdbiorcowDto, WiadomoscDto } from '@interfaces/komunikacja/wersje-robocze.interface';
import { WiadomoscOdebranaDto } from '@interfaces/komunikacja/wiadomosc-odebrana.interface';
import { WiadomoscWyslanaDto } from '@interfaces/komunikacja/wiadomosc-wyslana.interface';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GrupyOdbiorcowService } from '@services/api/komunikacja/grupy-odbiorcow.service';
import { KomunikacjaService } from '@services/api/komunikacja/komunikacja.service';
import { WiadomosciOdebraneService } from '@services/api/komunikacja/wiadomosci-odebrane.service';
import { WersjeRoboczeService } from '@services/api/komunikacja/wiadomosci-wersja-robocza.service';
import { WiadomosciWyslaneService } from '@services/api/komunikacja/wiadomosci-wyslane.service';
import { BehaviorSubject, repeat, Subject } from 'rxjs';

import { NotificationService } from '@components/notification/notification.service';
import { GrupaOdbiorcowFormComponent } from './grupy-odbiorcow/grupa-odbiorcow-form/grupa-odbiorcow-form.component';
import { NowaWiadomoscModalComponent } from './nowa-wiadomosc-modal/nowa-wiadomosc-modal.component';

@Injectable({ providedIn: 'root' })
export class WiadomosciFacade {
  readonly orderNavigtion = new Subject<void>();
  readonly requestNewData = new Subject<void>();
  readonly unreadMessagesCount = new BehaviorSubject(0);

  constructor(
    readonly grupyOdbiorcowService: GrupyOdbiorcowService,
    readonly komunikacjaService: KomunikacjaService,
    readonly wersjeRoboczeService: WersjeRoboczeService,
    readonly wiadomosciOdebraneService: WiadomosciOdebraneService,
    readonly wiadomosciWyslaneService: WiadomosciWyslaneService,
    readonly modalService: NgbModal,
    readonly notificationService: NotificationService,
  ) {}

  getNumberOfUnreadMessages(): void {
    this.wiadomosciOdebraneService
      .getNumberOfUnreadMessages()
      .pipe(repeat({ delay: 60000 }))
      .subscribe((data) => {
        this.unreadMessagesCount.next(data.liczbaNieodebranychWiadomosci);
      });
  }

  deleteIncomingMessages(v: string[], navigateBack = false): void {
    this.wiadomosciOdebraneService.delete({ wiadomoscIds: v }).subscribe(() => {
      this.requestNewData.next();
      this.notificationService.showDeleted();
      if (navigateBack) {
        this.orderNavigtion.next();
      }
    });
  }

  markAsRead(wiadomoscIds: string[]): void {
    this.wiadomosciOdebraneService.setMessageAsRead({ wiadomoscIds }).subscribe(() => {
      this.getNumberOfUnreadMessages();
      this.requestNewData.next();
    });
  }

  markAsUnread(wiadomoscIds: string[]): void {
    this.wiadomosciOdebraneService.setMessageAsUnread({ wiadomoscIds }).subscribe(() => {
      this.getNumberOfUnreadMessages();
      this.requestNewData.next();
    });
  }

  openWiadomoscForm(
    draft?: WiadomoscDto,
    replyingTo?: Partial<WiadomoscOdebranaDto & WiadomoscWyslanaDto>,
    nowiOdbiorcy?: UzytkownikIGrupaOdbiorcowDto[],
  ): void {
    const modal = this.modalService.open(NowaWiadomoscModalComponent, {
      modalDialogClass: 'modal-dialog-centered modal-dialog-scrollable modal-xl',
    });
    const componentInstance = modal.componentInstance as NowaWiadomoscModalComponent;
    componentInstance.recordData = draft;

    if (replyingTo?.odbiorcy?.length) {
      const { temat } = replyingTo;
      const newMsgData: Partial<UzytkownikIGrupaOdbiorcowDto & WiadomoscOdebranaDto & WiadomoscWyslanaDto> = {
        liczbaOdbiorcowWGrupie: null,
        odbiorcy: [],
        temat,
        typOdbiorcy: TypOdbiorcy.Uzytkownik,
      };
      replyingTo.odbiorcy.forEach((el) => {
        newMsgData.odbiorcy.push(el);
      });
      componentInstance.replyingTo = newMsgData;
    } else if (replyingTo) {
      const { nadawcaId, nadawcaNazwa, temat, id } = replyingTo;
      const newMsgData: Partial<UzytkownikIGrupaOdbiorcowDto & WiadomoscOdebranaDto & WiadomoscWyslanaDto> = {
        liczbaOdbiorcowWGrupie: null,
        odbiorcy: [
          {
            odbiorcaNazwa: nadawcaNazwa,
            odbiorcaId: nadawcaId,
            id,
          },
        ],
        temat,
        typOdbiorcy: TypOdbiorcy.Uzytkownik,
      };
      componentInstance.replyingTo = newMsgData;
    } else if (nowiOdbiorcy) {
      const newMsgData: Partial<UzytkownikIGrupaOdbiorcowDto & WiadomoscOdebranaDto & WiadomoscWyslanaDto> = {
        liczbaOdbiorcowWGrupie: null,
        odbiorcy: [],
        temat: '',
        typOdbiorcy: TypOdbiorcy.Uzytkownik,
      };

      nowiOdbiorcy.forEach((el) => {
        const odbiorca: OdbiorcaDto = {
          odbiorcaNazwa: el.nazwa,
          odbiorcaId: el.id,
          id: null,
        };
        newMsgData.odbiorcy.push(odbiorca);
      });

      componentInstance.replyingTo = newMsgData;
    }

    modal.closed.subscribe(() => {
      this.requestNewData.next();
    });
    modal.dismissed.subscribe(() => {
      this.requestNewData.next();
    });
  }

  deleteSentMessages(wiadomoscIds: string[], navigateBack = false): void {
    this.wiadomosciWyslaneService.delete({ wiadomoscIds }).subscribe(() => {
      this.requestNewData.next();
      this.notificationService.showDeleted();
      if (navigateBack) {
        this.orderNavigtion.next();
      }
    });
  }

  deleteDrafts(wiadomoscIds: string[]): void {
    this.wersjeRoboczeService.delete({ wiadomoscIds }).subscribe(() => {
      this.requestNewData.next();
      this.notificationService.showDeleted();
    });
  }

  deleteGroups(grupaOdbiorcowIds: string[], navigateBack = false): void {
    this.grupyOdbiorcowService.delete({ grupaOdbiorcowIds }).subscribe(() => {
      this.notificationService.showDeleted();
      if (navigateBack) {
        this.orderNavigtion.next();
      } else {
        this.requestNewData.next();
      }
    });
  }

  openGrupaForm(recordData?: GrupaOdbiorcowDto): void {
    const modal = this.modalService.open(GrupaOdbiorcowFormComponent);
    modal.componentInstance.recordData = recordData;
    modal.closed.subscribe(() => {
      this.requestNewData.next();
    });
  }

  extractContent(s: string): string {
    const span = document.createElement('span');
    span.innerHTML = s;
    return span.textContent || span.innerText;
  }
}
